import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Particle system component
const ParticleField: React.FC = () => {
  const ref = useRef<THREE.Points>(null);
  
  const particles = useMemo(() => {
    const geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(2000 * 3);
    const colors = new Float32Array(2000 * 3);
    
    for (let i = 0; i < 2000; i++) {
      positions[i * 3] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 1] = (Math.random() - 0.5) * 20;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 20;
      
      colors[i * 3] = Math.random() * 0.5 + 0.5; // R
      colors[i * 3 + 1] = Math.random() * 0.5 + 0.5; // G
      colors[i * 3 + 2] = Math.random() * 0.8 + 0.2; // B
    }
    
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    
    return geometry;
  }, []);

  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.x = state.clock.elapsedTime * 0.1;
      ref.current.rotation.y = state.clock.elapsedTime * 0.05;
    }
  });

  return (
    <points ref={ref} geometry={particles}>
      <pointsMaterial
        transparent
        vertexColors
        size={0.02}
        sizeAttenuation={true}
        depthWrite={false}
      />
    </points>
  );
};

// Floating geometric shapes
const FloatingShapes: React.FC = () => {
  const groupRef = useRef<THREE.Group>(null);

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y = state.clock.elapsedTime * 0.1;
      groupRef.current.position.y = Math.sin(state.clock.elapsedTime * 0.5) * 0.5;
    }
  });

  return (
    <group ref={groupRef} position={[0, 0, -5]}>
      {/* Large sphere */}
      <mesh position={[0, 0, 0]}>
        <sphereGeometry args={[2, 32, 32]} />
        <meshBasicMaterial 
          color="#0ea5e9" 
          transparent 
          opacity={0.1} 
          wireframe 
        />
      </mesh>
      
      {/* Smaller cubes */}
      <mesh position={[3, 2, 0]} rotation={[0.5, 0.3, 0]}>
        <boxGeometry args={[1, 1, 1]} />
        <meshBasicMaterial 
          color="#38bdf8" 
          transparent 
          opacity={0.2} 
          wireframe 
        />
      </mesh>
      
      <mesh position={[-3, -2, 0]} rotation={[-0.3, 0.7, 0]}>
        <boxGeometry args={[0.8, 0.8, 0.8]} />
        <meshBasicMaterial 
          color="#7dd3fc" 
          transparent 
          opacity={0.15} 
          wireframe 
        />
      </mesh>
      
      {/* Torus */}
      <mesh position={[0, 3, 0]} rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1, 0.3, 16, 32]} />
        <meshBasicMaterial 
          color="#bae6fd" 
          transparent 
          opacity={0.1} 
          wireframe 
        />
      </mesh>
    </group>
  );
};

// Main background scene component
const BackgroundScene: React.FC = () => {
  return (
    <div className="fixed inset-0 z-0">
      <Canvas
        camera={{ position: [0, 0, 5], fov: 75 }}
        style={{ background: 'transparent' }}
      >
        <ambientLight intensity={0.5} />
        <pointLight position={[10, 10, 10]} intensity={1} />
        
        <ParticleField />
        <FloatingShapes />
      </Canvas>
    </div>
  );
};

export default BackgroundScene; 